import { useQuery } from '@apollo/client';
import { gql } from '__generated__/gql';
import { FC, useState } from 'react';

import ErrorMessage from 'components/ErrorMessage';
import { FormInput, FormPanelWithReadMode } from 'components/FormPanel';

import Button from 'primitives/Button';
import LoadingIndicator from 'primitives/LoadingIndicator';
import SlideOver from 'primitives/SlideOver';

import AccreditationConfirmationDialogButton from './AccreditationConfirmationDialogButton';

const GET_INVESTMENT_ENTITY_DOCUMENT_QUERY = gql(`
  query InvestmentEntityDocument($id: ID!) {
    investmentEntityDocument(id: $id) {
      id
      type
      name
      description
      url
      verifyKyc
    }
  }
`);

const InvestmentEntityDocumentSlideOver: FC<{
  open: boolean;
  documentId: string;
  onClose: () => void;
  investmentEntityId: string;
}> = ({ open = false, documentId, onClose, investmentEntityId }) => {
  const [showAccreditationDialog, setShowAccreditationDialog] = useState(false);
  const [showAccreditationSubmissionMessage, setShowAccreditationSubmissionMessage] =
    useState(false);

  const { loading, error, data, refetch } = useQuery(GET_INVESTMENT_ENTITY_DOCUMENT_QUERY, {
    variables: {
      id: documentId,
    },
    skip: !open,
  });

  function renderContent() {
    if (loading) return <LoadingIndicator />;

    if (error || !data) return <ErrorMessage error={error} refetch={refetch} />;

    const investmentEntityDocument = data.investmentEntityDocument;

    return (
      <>
        <FormPanelWithReadMode
          loading={loading}
          error={error}
          onSubmit={() => {}}
          disableEdit={true}
          title="Investment Entity Document"
        >
          <FormInput
            type="text"
            fieldName="name"
            label="Name"
            defaultValue={investmentEntityDocument.name}
          />
        </FormPanelWithReadMode>
        <>
          {showAccreditationSubmissionMessage && (
            <p className="text-sm pb-6 pt-2 text-indigo-600">
              Thank you for confirming your accreditation status. If there are any changes, you can
              resubmit your details.
            </p>
          )}
          <Button
            onClick={() => {
              window.open(investmentEntityDocument.url, '_blank');
              if (investmentEntityDocument.verifyKyc) setShowAccreditationDialog(true);
            }}
            loading={loading}
          >
            View Document
          </Button>
        </>
      </>
    );
  }

  return (
    <>
      <SlideOver
        open={open}
        onClose={onClose}
        title="Investment Entity Document"
        subTitle="Manage and audit deal document details"
      >
        {renderContent()}
      </SlideOver>
      {showAccreditationDialog && (
        <AccreditationConfirmationDialogButton
          investmentEntityId={investmentEntityId}
          documentId={documentId}
          open={showAccreditationDialog}
          onClose={() => setShowAccreditationDialog(false)}
          setDisplayMessage={() => setShowAccreditationSubmissionMessage(true)}
        />
      )}
    </>
  );
};

export default InvestmentEntityDocumentSlideOver;
